import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, Spinner, Dialog } from '@/components/ui';

// ---------------------------------------------------------------------------
// VersionHistoryPanel (PRODOPSAI-661) — generic admin "Version history" list
// shared by MetricVersionHistory and PromptVersionHistory. Owns the load /
// loading / error / empty states, the newest-is-"Current" list rendering, the
// restore-confirm dialog, and the preview-dialog open state. Callers supply the
// data source (`load`), the restore action (`onRestore`), the source-tag label
// map (`sourceLabel`), and what the "View" modal renders (`renderPreview`).
// ---------------------------------------------------------------------------

export interface VersionLike {
  version: number;
  source: string;
  createdAt: string;
  createdBy: string | null;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

interface VersionHistoryPanelProps<TVersion extends VersionLike> {
  /** Load the version list (newest first). Memoise on the subject key so the
   *  panel reloads when the subject changes. */
  load: () => Promise<TVersion[]>;
  /** Perform the restore; the panel reloads the list afterwards. */
  onRestore: (version: number) => Promise<void>;
  /** Map a source tag to a human label. */
  sourceLabel: (source: string) => string;
  /** Render the "View" modal for a version; the caller owns the modal + close. */
  renderPreview: (version: TVersion, onClose: () => void) => ReactNode;
  /** Optional test id for the list element. */
  listTestId?: string;
}

export function VersionHistoryPanel<TVersion extends VersionLike>({
  load,
  onRestore,
  sourceLabel,
  renderPreview,
  listTestId,
}: Readonly<VersionHistoryPanelProps<TVersion>>) {
  const [versions, setVersions] = useState<TVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmVersion, setConfirmVersion] = useState<number | null>(null);
  const [previewVersion, setPreviewVersion] = useState<TVersion | null>(null);
  const [restoring, setRestoring] = useState(false);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Drop any previously-loaded list so a different subject (or a failed
    // reload) never shows another subject's stale history.
    setVersions([]);
    try {
      setVersions(await load());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [load]);

  useEffect(() => {
    void loadVersions();
  }, [loadVersions]);

  async function handleRestore(version: number) {
    setConfirmVersion(null);
    setRestoring(true);
    setError(null);
    try {
      await onRestore(version);
      await loadVersions();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Restore failed');
    } finally {
      setRestoring(false);
    }
  }

  const isEmpty = !loading && error === null && versions.length === 0;

  return (
    <div className="mt-4 border-t border-border pt-3">
      <h4 className="mb-2 text-xs font-semibold text-foreground">Version history</h4>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Spinner /> Loading…
        </div>
      )}
      {error && <p className="text-xs text-error" role="alert">{error}</p>}
      {isEmpty && <p className="text-xs text-muted-foreground">No previous versions yet.</p>}

      {versions.length > 0 && (
        <ul className="space-y-1" data-testid={listTestId}>
          {versions.map((v, i) => (
            <li
              key={v.version}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5 text-xs"
            >
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">v{v.version}</span>
                {i === 0 && (
                  <span className="ml-1 rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary">
                    Current
                  </span>
                )}
                {' · '}{sourceLabel(v.source)}
                {' · '}{formatWhen(v.createdAt)}
                {v.createdBy ? ` · ${v.createdBy}` : ''}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => setPreviewVersion(v)}>
                  View
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={restoring}
                  onClick={() => setConfirmVersion(v.version)}
                >
                  Restore
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {previewVersion && renderPreview(previewVersion, () => setPreviewVersion(null))}

      <Dialog
        open={confirmVersion !== null}
        onClose={() => setConfirmVersion(null)}
        title="Restore this version?"
      >
        <p className="text-sm text-muted-foreground">
          Restore <strong>version {confirmVersion}</strong>? The current text is saved as a new
          version first, so you can undo this — restoring then replaces the editor contents,
          discarding any unsaved edits there.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setConfirmVersion(null)}>Cancel</Button>
          <Button
            size="sm"
            disabled={restoring}
            onClick={() => { if (confirmVersion !== null) void handleRestore(confirmVersion); }}
          >
            {restoring ? 'Restoring…' : 'Restore'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

export default VersionHistoryPanel;
